import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight } from "lucide-react";
import { UnifiedFilters } from "@/components/UnifiedFilters";
import { HeroSearch } from "@/components/HeroSearch";
import api from "@/lib/api";

/**
 * Home-page filter panel + search bar. Same UnifiedFilters the Search and
 * Dispensary menu pages use — so the whole app feels like one product.
 *
 * On Home, filter chips DO NOT auto-navigate. The user composes the search
 * (pick Vape + Modern Flower brand + THC 20%+ + in-stock) and taps the
 * Search button to jump to /search with every selection carried through
 * the URL. Category counts come from /api/hero-stats; brand list comes
 * from /api/brands.
 */
const DEFAULT_FILTERS = {
  category: "",
  subcategory: "",
  size: "",
  type: "",
  brand: "",
  thc_min: 0,
  price_max: 200,
  in_stock_only: false,
};

export const HomeCategoryChips = () => {
  const [counts, setCounts] = useState(null);
  const [brands, setBrands] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/hero-stats")
      .then((r) => !cancelled && setCounts(r.data?.categories || {}))
      .catch(() => {});
    api
      .get("/brands", { params: { limit: 30 } })
      .then((r) => !cancelled && setBrands(r.data?.brands || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!counts) return null;

  return (
    <div
      data-testid="home-category-chips"
      className="max-w-2xl mx-auto text-left space-y-3"
    >
      {/* Trending quick-shortcut — one tap to the highest-THC products in
          stock right now. Sits above the filter panel so it reads as a
          "power move" for returning users. */}
      <Link
        to="/search?sort=thc_desc&in_stock_only=1"
        data-testid="home-trending-chip"
        className="group flex items-center gap-2.5 rounded-full pl-3 pr-4 h-10 w-fit border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20 hover:border-amber-400/60 transition-colors"
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/25">
          <Flame className="w-3.5 h-3.5" strokeWidth={2} />
        </span>
        <span className="text-xs sm:text-sm font-medium tracking-wide">
          Trending — highest THC in stock now
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-amber-300 -mr-1 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
      </Link>

      {/* Search bar FIRST — was landing below the filter grid on mobile,
          pushing it off the fold. Now sits right under the Trending chip
          so users can type-or-tap without scrolling. Filters below still
          feed into the search submit URL. */}
      <HeroSearch filters={filters} />

      <UnifiedFilters
        filters={filters}
        setFilters={setFilters}
        onReset={() => setFilters(DEFAULT_FILTERS)}
        catCounts={counts}
        brandOptions={brands || []}
        testIdPrefix="home-filter"
        className="bg-card/70 backdrop-blur"
      />
    </div>
  );
};

export default HomeCategoryChips;
